const ACTION_TYPE_MAP: Record<string, string> = {
  contact_sms_inbound_message_received:
    'contacts.lastAction.types.contactSmsInboundMessageReceived',
  contact_sms_outbound_message_sent: 'contacts.lastAction.types.contactSmsOutboundMessageSent',
  contact_email_clicked: 'contacts.lastAction.types.contactEmailClicked',
  contact_email_inbox_opened: 'contacts.lastAction.types.contactEmailInboxOpened',
  contact_page_visited: 'contacts.lastAction.types.contactPageVisited',
  contact_line_inbound_message_received:
    'contacts.lastAction.types.contactLineInboundMessageReceived',
  contact_line_clicked: 'contacts.lastAction.types.contactLineClicked',
  contact_sms_link_clicked: 'contacts.lastAction.types.contactSmsLinkClicked',
};

const MINUTE_IN_MS = 60 * 1000;
const HOUR_IN_MS = 60 * MINUTE_IN_MS;
const DAY_IN_MS = 24 * HOUR_IN_MS;
const MONTH_IN_MS = 30 * DAY_IN_MS;
const YEAR_IN_MS = 365 * DAY_IN_MS;

interface FormatLastActionParams {
  readonly lastActionType: string | null | undefined;
  readonly lastActionOccurredAt: string | null | undefined;
  readonly t: (key: string, options?: Record<string, unknown>) => string;
}

const formatRelativeTime = (
  occurredAt: string,
  t: (key: string, options?: Record<string, unknown>) => string,
): string | null => {
  const timestamp = new Date(occurredAt).getTime();

  if (Number.isNaN(timestamp)) {
    return null;
  }

  const diffInMs = Math.max(0, Date.now() - timestamp);

  if (diffInMs < MINUTE_IN_MS) {
    return t('contacts.lastAction.time.justNow');
  }

  if (diffInMs < HOUR_IN_MS) {
    const count = Math.floor(diffInMs / MINUTE_IN_MS);

    return t('contacts.lastAction.time.minutesAgo', { count });
  }

  if (diffInMs < DAY_IN_MS) {
    const count = Math.floor(diffInMs / HOUR_IN_MS);

    return t('contacts.lastAction.time.hoursAgo', { count });
  }

  if (diffInMs < MONTH_IN_MS) {
    const count = Math.floor(diffInMs / DAY_IN_MS);

    return t('contacts.lastAction.time.daysAgo', { count });
  }

  if (diffInMs < YEAR_IN_MS) {
    const count = Math.floor(diffInMs / MONTH_IN_MS);

    return t('contacts.lastAction.time.monthsAgo', { count });
  }

  const count = Math.floor(diffInMs / YEAR_IN_MS);

  return t('contacts.lastAction.time.yearsAgo', { count });
};

const formatLastAction = ({
  lastActionType,
  lastActionOccurredAt,
  t,
}: FormatLastActionParams): string | null => {
  if (!lastActionType || !lastActionOccurredAt) {
    return null;
  }

  const actionKey = ACTION_TYPE_MAP[lastActionType] ?? 'contacts.lastAction.types.unknown';
  const time = formatRelativeTime(lastActionOccurredAt, t);

  if (!time) {
    return null;
  }

  const action = t(actionKey);

  return t('contacts.lastAction.label', { action, time });
};

export { formatLastAction };
export type { FormatLastActionParams };
